<template>
  <RevStatusTracker
    :current-step
    orientation="vertical"
    progress-bar-color="success"
    start-with="last"
    :steps
  >
    <template v-for="step in steps" :key="step.key" #[`${step.key}-content`]>
      <div class="flex text-action-default-low">
        <p class="caption grow">
          <span v-if="step.date" class="block">
            {{ formatStepDateTime(step.date) }}
          </span>
          <template v-if="step.address">
            <span v-if="formatAddressRecipient(step.address)" class="block">
              {{ formatAddressRecipient(step.address) }}
            </span>
            <span class="block">
              {{ formatAddressWithoutRecipient(step.address) }}
            </span>
          </template>
        </p>
        <RevTooltip
          v-if="step.name === 'AVAILABLE_FOR_COLLECTION' && step.address"
          class="shrink-0 flex items-center"
          position="top"
        >
          <template #trigger="{ show, hide }">
            <RevButtonIcon
              :aria-label="i18n(translations.copyAddress)"
              :icon="IconCopy"
              size="small"
              variant="ghost"
              @click="handleCopyAddress(step.address, show, hide)"
              @focusout="hide"
              @mouseleave="hide"
            />
          </template>
          {{ i18n(translations.addressCopied) }}
        </RevTooltip>
      </div>
    </template>
  </RevStatusTracker>
</template>

<script setup lang="ts">
import type { Address } from '@backmarket/nuxt-module-address/address'
import { useFormatAddress } from '@backmarket/nuxt-module-address/useFormatAddress'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevStatusTracker } from '@ds/components/StatusTracker'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconCopy } from '@ds/icons/IconCopy'

import translations from './DeliveryTimeline.translations'
import type { StatusTrackerStep } from './DeliveryTimeline.types'

defineProps<{
  steps: StatusTrackerStep[]
  currentStep: string
}>()

const i18n = useI18n()

const formatStepDateTime = (date: string) => {
  const dateObj = new Date(date)
  const formattedDate = i18n.date(dateObj, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const formattedTime = i18n.date(dateObj, {
    hour: '2-digit',
    minute: '2-digit',
  })

  return `${formattedDate}, ${formattedTime}`
}

const formatAddressRecipient = (address: Address) => {
  if (!address) return ''

  const name = [address.firstName, address.lastName].filter(Boolean).join(' ')

  return name || address.company || ''
}

const formatAddressWithoutRecipient = (address: Address) => {
  if (!address) return ''

  const lines = useFormatAddress(address, {
    includeName: false,
    includeCompany: false,
  })

  return lines.join(', ')
}

const formatAddressInline = (address: Address) => {
  if (!address) return ''

  const recipient = formatAddressRecipient(address)
  const addressWithoutRecipient = formatAddressWithoutRecipient(address)

  return recipient
    ? `${recipient}, ${addressWithoutRecipient}`
    : addressWithoutRecipient
}

const handleCopyAddress = async (
  address: Address,
  show: () => void,
  hide: () => void,
) => {
  try {
    await navigator.clipboard.writeText(formatAddressInline(address))
    show()

    setTimeout(() => {
      hide()
    }, 1000)
  } catch {
    // Do nothing
  }
}
</script>
